/**
 * Map ADL 1.4 operational template XML (OPT) constraint nodes to AOM instances.
 */

import { XMLParser } from "fast-xml-parser";
import * as openehr_am from "../../am/openehr_am.ts";
import * as openehr_base from "../../base/openehr_base.ts";
import {
  COMPONENT_TERM_DEFINITIONS_KEY,
  type TermScopeMeta,
} from "../../generation/term_scope.ts";
import type { TermBag } from "../../am/util/ontology_merge.ts";

export function parseLegacyTemplateXml(xml: string): Record<string, unknown> {
  const parser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: "@_",
    removeNSPrefix: true,
    parseTagValue: true,
    trimValues: true,
    isArray: (_name, jpath) => {
      const p = jpath as string;
      return p.endsWith(".attributes") ||
        p.endsWith(".children") ||
        p.endsWith(".code_list") ||
        p.endsWith(".list") ||
        p.endsWith(".term_definitions") ||
        p.endsWith(".items") ||
        // Top-level OPT `<annotations path="...">` repeats as siblings.
        p === "template.annotations" ||
        p.endsWith(".annotations");
    },
  });
  const doc = parser.parse(xml) as Record<string, unknown>;
  const root = doc.template ?? doc.OPERATIONALTEMPLATE ??
    doc.operationaltemplate;
  if (!root || typeof root !== "object") {
    throw new Error("Expected root <template> element");
  }
  return root as Record<string, unknown>;
}

export function asArray<T>(value: T | T[] | undefined | null): T[] {
  if (value === undefined || value === null) return [];
  return Array.isArray(value) ? value : [value];
}

export function textValue(node: unknown, key = "value"): string | undefined {
  if (node === undefined || node === null) return undefined;
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (typeof node === "object") {
    const rec = node as Record<string, unknown>;
    if (rec[key] !== undefined) return textValue(rec[key], key);
    if (rec.code_string !== undefined) return String(rec.code_string);
    const text = rec["#text"];
    if (text !== undefined && text !== null) {
      if (typeof text === "string" || typeof text === "number") {
        return String(text);
      }
      if (Array.isArray(text)) {
        const joined = text
          .map((
            part,
          ) => (typeof part === "string" || typeof part === "number"
            ? String(part)
            : "")
          )
          .join("")
          .trim();
        if (joined) return joined;
      }
    }
  }
  return undefined;
}

export function xsiType(node: Record<string, unknown>): string {
  const t = node["@_type"] ?? node["@_xsi:type"] ?? "";
  return String(t).replace(/^.*:/, "");
}

/** Store a plain number bound on a Multiplicity_interval (typed Integer). */
function setBound(
  m: openehr_base.Multiplicity_interval,
  key: "lower" | "upper",
  value: number,
): void {
  (m as unknown as Record<string, unknown>)[key] = value;
}

export function parseMultiplicity(
  node: unknown,
): openehr_base.Multiplicity_interval | undefined {
  if (!node || typeof node !== "object") return undefined;
  const n = node as Record<string, unknown>;
  const interval = (n.interval ?? n) as Record<string, unknown>;
  const m = new openehr_base.Multiplicity_interval();
  // Runtime convention: plain numbers in lower/upper (declared as Integer)
  if (interval.lower !== undefined) {
    setBound(m, "lower", Number(interval.lower));
  }
  if (interval.upper !== undefined) {
    setBound(m, "upper", Number(interval.upper));
  }
  if (interval.lower_unbounded !== undefined) {
    m.lower_unbounded = interval.lower_unbounded === true ||
      interval.lower_unbounded === "true";
  }
  if (interval.upper_unbounded !== undefined) {
    m.upper_unbounded = interval.upper_unbounded === true ||
      interval.upper_unbounded === "true";
  }
  if (interval.lower_included !== undefined) {
    m.lower_included = interval.lower_included === true ||
      interval.lower_included === "true";
  }
  if (interval.upper_included !== undefined) {
    m.upper_included = interval.upper_included === true ||
      interval.upper_included === "true";
  }
  return m;
}

export function parseCardinality(
  node: unknown,
): openehr_am.CARDINALITY | undefined {
  if (!node || typeof node !== "object") return undefined;
  const n = node as Record<string, unknown>;
  const card = new openehr_am.CARDINALITY();
  if (n.is_ordered !== undefined) {
    card.is_ordered = n.is_ordered === true || n.is_ordered === "true";
  }
  if (n.is_unique !== undefined) {
    card.is_unique = n.is_unique === true || n.is_unique === "true";
  }
  card.interval = parseMultiplicity(n.interval ?? n);
  return card;
}

function defaultContainerCardinality(): openehr_am.CARDINALITY {
  const card = new openehr_am.CARDINALITY();
  const interval = new openehr_base.Multiplicity_interval();
  setBound(interval, "lower", 0);
  interval.upper_unbounded = true;
  card.interval = interval;
  return card;
}

function mapPrimitiveType(xsi: string): string {
  const map: Record<string, string> = {
    C_DV_QUANTITY: "C_QUANTITY",
    C_QUANTITY: "C_QUANTITY",
    C_DV_CODED_TEXT: "C_CODED_TEXT",
    C_CODED_TEXT: "C_CODED_TEXT",
    C_CODE_PHRASE: "C_TERMINOLOGY_CODE",
    C_TERMINOLOGY_CODE: "C_TERMINOLOGY_CODE",
    C_DV_TEXT: "C_STRING",
    C_STRING: "C_STRING",
    C_INTEGER: "C_INTEGER",
    C_REAL: "C_REAL",
    C_BOOLEAN: "C_BOOLEAN",
    C_DV_DATE_TIME: "C_DATE_TIME",
    C_DATE_TIME: "C_DATE_TIME",
    C_DV_DATE: "C_DATE",
    C_DATE: "C_DATE",
    C_DV_TIME: "C_TIME",
    C_TIME: "C_TIME",
    C_DV_DURATION: "C_DURATION",
    C_DURATION: "C_DURATION",
    C_DV_ORDINAL: "C_ORDINAL",
    C_ORDINAL: "C_ORDINAL",
    C_DV_SCALE: "C_SCALE",
    C_SCALE: "C_SCALE",
  };
  return map[xsi] ?? xsi;
}

export function parseCObject(node: unknown): openehr_am.C_OBJECT {
  if (!node || typeof node !== "object") {
    throw new Error("Invalid C_OBJECT node");
  }
  const n = node as Record<string, unknown>;
  const type = (xsiType(n) || amFieldString(n, "rm_type_name", "rmTypeName")) ??
    "C_COMPLEX_OBJECT";
  const hasArchetypeId = !!(
    textValue(n.archetype_id) ??
      textValue(n.archetype_ref) ??
      textValue(n.archetypeRef)
  );

  // Legacy OPT XML often omits xsi:type on <definition> even when the node
  // carries archetype_id + term_definitions (a C_ARCHETYPE_ROOT).
  if (type === "C_ARCHETYPE_ROOT" || hasArchetypeId) {
    return parseCArchetypeRoot(n);
  }
  // `ARCHETYPE_SLOT` does not start with `C_`. It must be recognised before
  // the generic non-`C_` fallback, which would otherwise drop include/exclude.
  if (type === "ARCHETYPE_SLOT") {
    return parseArchetypeSlot(n);
  }
  if (type === "C_COMPLEX_OBJECT" || !type.startsWith("C_")) {
    return parseCComplexObject(n);
  }

  if (type === "C_PRIMITIVE_OBJECT") {
    return parseCPrimitiveObject(n);
  }

  // ehrtslib up to 0.10 wrote an open CODE_PHRASE constraint as
  // `C_DV_TEXT`; the RM type, not the xsi type, says what it constrains.
  if (amFieldString(n, "rm_type_name", "rmTypeName") === "CODE_PHRASE") {
    return parseCTerminologyCode(n);
  }
  const mapped = mapPrimitiveType(type);
  if (mapped === "C_QUANTITY") return parseCQuantity(n);
  if (mapped === "C_TERMINOLOGY_CODE") return parseCTerminologyCode(n);
  if (mapped === "C_CODED_TEXT") return parseCCodedText(n);
  if (mapped === "C_ORDINAL") return parseCOrdinal(n);
  if (mapped === "C_SCALE") return parseCScale(n);
  // The primitive constraint classes do not extend C_OBJECT in the generated
  // model but carry equivalent runtime metadata (see ConstraintMeta).
  const primitive = parsePrimitiveItem(n, mapped);
  if (primitive) {
    return primitive as unknown as openehr_am.C_OBJECT;
  }

  const fallback = new openehr_am.C_PRIMITIVE_OBJECT();
  fallback.rm_type_name = String(n.rm_type_name ?? type.replace(/^C_/, "DV_"));
  return fallback;
}

function parseCPrimitiveObject(
  n: Record<string, unknown>,
): openehr_am.C_PRIMITIVE_OBJECT {
  const obj = new openehr_am.C_PRIMITIVE_OBJECT();
  applyOccurrence(obj, n);
  if (!obj.rm_type_name) {
    obj.rm_type_name = String(n.rm_type_name ?? "Any");
  }
  const itemNode = n.item;
  if (itemNode && typeof itemNode === "object") {
    const rec = itemNode as Record<string, unknown>;
    const itemType = xsiType(rec) ||
      amFieldString(rec, "rm_type_name", "rmTypeName") ||
      "";
    const mapped = mapPrimitiveType(itemType);
    const item = parsePrimitiveItem(rec, mapped);
    if (item) obj.item = item;
  }
  return obj;
}

function parsePrimitiveItem(
  n: Record<string, unknown>,
  mapped: string,
): openehr_am.C_PRIMITIVE | undefined {
  if (mapped === "C_STRING") return parseCString(n);
  if (mapped === "C_INTEGER") return parseCInteger(n);
  if (mapped === "C_REAL") return parseCReal(n);
  if (mapped === "C_BOOLEAN") return parseCBoolean(n);
  if (mapped === "C_DATE") return parseCDate(n);
  if (mapped === "C_TIME") return parseCTime(n);
  if (mapped === "C_DATE_TIME") return parseCDateTime(n);
  if (mapped === "C_DURATION") return parseCDuration(n);
  return undefined;
}

export function parseOccurrencesOrMultiplicity(
  val: unknown,
): openehr_base.Multiplicity_interval | undefined {
  if (typeof val === "string") {
    const s = val.trim();
    const star = s.match(/^(\d+|\*)\.\.(\d+|\*)$/);
    if (star) {
      const m = new openehr_base.Multiplicity_interval();
      const lo = star[1];
      const hi = star[2];
      if (lo === "*") m.lower_unbounded = true;
      else setBound(m, "lower", Number(lo));
      if (hi === "*") m.upper_unbounded = true;
      else setBound(m, "upper", Number(hi));
      return m;
    }
  }
  return parseMultiplicity(val);
}

function amFieldString(
  n: Record<string, unknown>,
  ...keys: string[]
): string | undefined {
  for (const k of keys) {
    const v = n[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      return String(v);
    }
  }
  return undefined;
}

/**
 * Structural view of constraint-node metadata. The AM primitive constraint
 * classes (C_STRING, C_INTEGER, …) do not extend C_OBJECT in the generated
 * model, but the legacy OPT mapper decorates them with the same metadata at
 * runtime so downstream consumers can treat them uniformly.
 */
type ConstraintMeta = {
  occurrences?: openehr_base.Multiplicity_interval;
  rm_type_name?: string;
  node_id?: string;
  range?: openehr_base.Multiplicity_interval;
};

function meta(target: unknown): ConstraintMeta {
  return target as ConstraintMeta;
}

function applyOccurrence(
  target: ConstraintMeta,
  n: Record<string, unknown>,
): void {
  const rm = amFieldString(n, "rm_type_name", "rmTypeName");
  if (rm) target.rm_type_name = rm;
  const nodeId = amFieldString(n, "node_id", "nodeId");
  if (nodeId) target.node_id = nodeId;
  target.occurrences = parseOccurrencesOrMultiplicity(n.occurrences);
}

function collectSlotPatterns(node: unknown, out: string[]): void {
  if (!node || typeof node !== "object") return;
  if (Array.isArray(node)) {
    for (const item of node) collectSlotPatterns(item, out);
    return;
  }
  const rec = node as Record<string, unknown>;
  if (typeof rec.pattern === "string" && rec.pattern.trim()) {
    out.push(rec.pattern.trim());
  }
  for (const value of Object.values(rec)) {
    if (value && typeof value === "object") collectSlotPatterns(value, out);
  }
}

function slotAssertions(
  node: unknown,
): openehr_am.ARCHETYPE_ID_CONSTRAINT[] {
  const patterns: string[] = [];
  for (const entry of asArray(node)) collectSlotPatterns(entry, patterns);
  return patterns.map((pattern) => {
    const constraint = new openehr_am.ARCHETYPE_ID_CONSTRAINT();
    const str = new openehr_am.C_STRING();
    str.pattern = pattern;
    constraint.constraint = str;
    return constraint;
  });
}

function parseArchetypeSlot(
  n: Record<string, unknown>,
): openehr_am.ARCHETYPE_SLOT {
  const slot = new openehr_am.ARCHETYPE_SLOT();
  applyOccurrence(slot, n);
  if (!slot.rm_type_name) slot.rm_type_name = "CLUSTER";
  const includes = slotAssertions(n.includes);
  const excludes = slotAssertions(n.excludes);
  if (includes.length) {
    (slot as unknown as { includes: openehr_am.ARCHETYPE_ID_CONSTRAINT[] })
      .includes = includes;
  }
  if (excludes.length) {
    (slot as unknown as { excludes: openehr_am.ARCHETYPE_ID_CONSTRAINT[] })
      .excludes = excludes;
  }
  return slot;
}

function parseCComplexObject(
  n: Record<string, unknown>,
): openehr_am.C_COMPLEX_OBJECT {
  const obj = new openehr_am.C_COMPLEX_OBJECT();
  applyOccurrence(obj, n);
  obj.attributes = asArray(n.attributes).map((attr) =>
    parseAttribute(attr, obj.rm_type_name)
  ).filter(
    Boolean,
  ) as openehr_am.C_ATTRIBUTE[];
  return obj;
}

function parseCArchetypeRoot(
  n: Record<string, unknown>,
): openehr_am.C_ARCHETYPE_ROOT {
  const root = new openehr_am.C_ARCHETYPE_ROOT();
  applyOccurrence(root, n);
  root.archetype_ref = textValue(n.archetype_id) ??
    textValue(n.archetype_ref) ??
    textValue(n.archetypeRef);
  root.attributes = asArray(n.attributes).map((attr) =>
    parseAttribute(attr, root.rm_type_name)
  ).filter(
    Boolean,
  ) as openehr_am.C_ATTRIBUTE[];
  const localTerms = collectLocalTermDefinitions(n);
  if (Object.keys(localTerms).length) {
    (root as TermScopeMeta)[COMPONENT_TERM_DEFINITIONS_KEY] = localTerms;
  }
  return root;
}

const CONTAINER_RM_ATTRIBUTES: Record<string, string[]> = {
  COMPOSITION: ["content"],
  SECTION: ["items"],
  CLUSTER: ["items"],
  ITEM_TREE: ["items"],
  ITEM_LIST: ["items"],
  ITEM_TABLE: ["rows"],
  HISTORY: ["events"],
  EVENT: ["data", "state"],
  POINT_EVENT: ["data", "state"],
  INTERVAL_EVENT: ["data", "state"],
  INSTRUCTION: ["activities"],
};

function isContainerRmAttribute(
  parentRmType: string | undefined,
  attrName: string,
): boolean {
  if (!parentRmType) return false;
  return (CONTAINER_RM_ATTRIBUTES[parentRmType] ?? []).includes(attrName);
}

export function parseAttribute(
  node: unknown,
  parentRmType?: string,
): openehr_am.C_ATTRIBUTE | null {
  if (!node || typeof node !== "object") return null;
  const n = node as Record<string, unknown>;
  const type = xsiType(n);
  const attrName = amFieldString(n, "rm_attribute_name", "rmAttributeName") ??
    "";
  const useMultiple = type === "C_MULTIPLE_ATTRIBUTE" ||
    ((type === "C_ATTRIBUTE" || type === "") &&
      isContainerRmAttribute(parentRmType, attrName));
  const attr = useMultiple
    ? new openehr_am.C_MULTIPLE_ATTRIBUTE()
    : new openehr_am.C_SINGLE_ATTRIBUTE();

  attr.rm_attribute_name = attrName;
  (attr as { existence?: openehr_base.Multiplicity_interval }).existence =
    parseOccurrencesOrMultiplicity(n.existence);

  if (attr instanceof openehr_am.C_MULTIPLE_ATTRIBUTE) {
    attr.cardinality = parseCardinality(n.cardinality) ??
      defaultContainerCardinality();
  }

  const children = asArray(n.children).map(parseCObject);
  if (children.length) {
    (attr as { children: openehr_am.C_OBJECT[] }).children = children;
  }
  return attr;
}

function parseCString(n: Record<string, unknown>): openehr_am.C_STRING {
  const s = new openehr_am.C_STRING();
  applyOccurrence(meta(s), n);
  if (!meta(s).rm_type_name) meta(s).rm_type_name = "STRING";
  if (n.pattern) s.pattern = String(n.pattern);
  const lists = asArray(n.list).map((x) =>
    String((x as Record<string, unknown>).value ?? x)
  );
  if (lists.length) (s as { list?: string[] }).list = lists;
  return s;
}

function constraintToRange(
  n: Record<string, unknown>,
): openehr_base.Multiplicity_interval | undefined {
  const direct = parseOccurrencesOrMultiplicity(n.range);
  if (direct) return direct;
  const constraints = asArray(n.constraint);
  if (!constraints.length) return undefined;
  const first = constraints[0];
  if (first && typeof first === "object") {
    return parseOccurrencesOrMultiplicity(first);
  }
  return undefined;
}

function numericList(n: Record<string, unknown>): number[] {
  return asArray(n.list).map((x) => {
    if (typeof x === "number") return x;
    const rec = x as Record<string, unknown>;
    return Number(rec.value ?? x);
  }).filter((x) => Number.isFinite(x));
}

function parseCInteger(n: Record<string, unknown>): openehr_am.C_INTEGER {
  const i = new openehr_am.C_INTEGER();
  applyOccurrence(meta(i), n);
  if (!meta(i).rm_type_name) meta(i).rm_type_name = "INTEGER";
  const range = constraintToRange(n);
  if (range) {
    meta(i).range = range;
    (i as { range?: openehr_base.Multiplicity_interval }).range = range;
  }
  const lists = numericList(n);
  if (lists.length) (i as { list?: number[] }).list = lists;
  return i;
}

function parseCReal(n: Record<string, unknown>): openehr_am.C_REAL {
  const r = new openehr_am.C_REAL();
  applyOccurrence(meta(r), n);
  if (!meta(r).rm_type_name) meta(r).rm_type_name = "REAL";
  const range = constraintToRange(n);
  if (range) {
    meta(r).range = range;
    (r as { range?: openehr_base.Multiplicity_interval }).range = range;
  }
  const lists = numericList(n);
  if (lists.length) (r as { list?: number[] }).list = lists;
  return r;
}

type TemporalConstraint = {
  pattern?: string;
  range?: openehr_base.Multiplicity_interval;
};

function applyTemporalPattern(
  target: TemporalConstraint,
  n: Record<string, unknown>,
): void {
  const pattern = n.pattern ?? n.pattern_constraint;
  if (pattern !== undefined && pattern !== null && String(pattern).length) {
    target.pattern = String(pattern);
  }
  const range = parseOrderedRange(n);
  if (range) target.range = range;
}

function parseOrderedRange(
  n: Record<string, unknown>,
): openehr_base.Multiplicity_interval | undefined {
  const direct = n.range;
  if (!direct || typeof direct !== "object") return constraintToRange(n);
  const rec = direct as Record<string, unknown>;
  const m = new openehr_base.Multiplicity_interval();
  if (rec.lower !== undefined) {
    (m as unknown as Record<string, unknown>).lower = rec.lower;
  }
  if (rec.upper !== undefined) {
    (m as unknown as Record<string, unknown>).upper = rec.upper;
  }
  if (rec.lower_unbounded !== undefined) {
    m.lower_unbounded = rec.lower_unbounded === true ||
      rec.lower_unbounded === "true";
  }
  if (rec.upper_unbounded !== undefined) {
    m.upper_unbounded = rec.upper_unbounded === true ||
      rec.upper_unbounded === "true";
  }
  if (rec.lower_included !== undefined) {
    m.lower_included = rec.lower_included === true ||
      rec.lower_included === "true";
  }
  if (rec.upper_included !== undefined) {
    m.upper_included = rec.upper_included === true ||
      rec.upper_included === "true";
  }
  return m;
}

function parseCDate(n: Record<string, unknown>): openehr_am.C_DATE {
  const d = new openehr_am.C_DATE();
  applyOccurrence(meta(d), n);
  if (!meta(d).rm_type_name) meta(d).rm_type_name = "DATE";
  applyTemporalPattern(d as TemporalConstraint, n);
  return d;
}

function parseCTime(n: Record<string, unknown>): openehr_am.C_TIME {
  const t = new openehr_am.C_TIME();
  applyOccurrence(meta(t), n);
  if (!meta(t).rm_type_name) meta(t).rm_type_name = "TIME";
  applyTemporalPattern(t as TemporalConstraint, n);
  return t;
}

function parseCDateTime(n: Record<string, unknown>): openehr_am.C_DATE_TIME {
  const t = new openehr_am.C_DATE_TIME();
  applyOccurrence(meta(t), n);
  if (!meta(t).rm_type_name) meta(t).rm_type_name = "DATE_TIME";
  applyTemporalPattern(t as TemporalConstraint, n);
  return t;
}

function parseCDuration(n: Record<string, unknown>): openehr_am.C_DURATION {
  const d = new openehr_am.C_DURATION();
  applyOccurrence(meta(d), n);
  if (!meta(d).rm_type_name) meta(d).rm_type_name = "DURATION";
  applyTemporalPattern(d as TemporalConstraint, n);
  return d;
}

function parseCBoolean(n: Record<string, unknown>): openehr_am.C_BOOLEAN {
  const b = new openehr_am.C_BOOLEAN();
  applyOccurrence(meta(b), n);
  if (n.true_valid !== undefined) {
    b.true_valid = n.true_valid === true || n.true_valid === "true";
  }
  if (n.false_valid !== undefined) {
    b.false_valid = n.false_valid === true || n.false_valid === "true";
  }
  return b;
}

/** Runtime extras on ADL 1.4 `C_CODE_PHRASE` mapped to AOM2 `C_TERMINOLOGY_CODE`. */
type TerminologyCodeRuntime = openehr_am.C_TERMINOLOGY_CODE & {
  code_list?: string[];
  terminology_id?: string;
};

function terminologyIdValue(tid: unknown): string | undefined {
  if (typeof tid === "string") return tid;
  if (tid && typeof tid === "object") {
    return textValue(tid as Record<string, unknown>);
  }
  return undefined;
}

function parseAssumedTerminologyCode(
  node: unknown,
): openehr_base.Terminology_code | undefined {
  if (!node || typeof node !== "object") return undefined;
  const rec = node as Record<string, unknown>;
  const code = textValue(rec.code_string) ??
    (typeof rec.code_string === "string" || typeof rec.code_string === "number"
      ? String(rec.code_string)
      : undefined);
  if (!code) return undefined;
  const tc = new openehr_base.Terminology_code();
  tc.code_string = code;
  const tid = terminologyIdValue(rec.terminology_id ?? rec.terminologyId);
  if (tid) tc.terminology_id = tid;
  return tc;
}

function parseCTerminologyCode(
  n: Record<string, unknown>,
): openehr_am.C_TERMINOLOGY_CODE {
  const t = new openehr_am.C_TERMINOLOGY_CODE() as TerminologyCodeRuntime;
  applyOccurrence(t, n);
  t.rm_type_name = "CODE_PHRASE";

  const tid = terminologyIdValue(n.terminology_id ?? n.terminologyId);
  if (tid) t.terminology_id = tid;

  const fromList = asArray(n.code_list).map(String);
  const fromConstraint = asArray(n.constraint).filter((x) =>
    typeof x === "string"
  ).map(String);
  const codes = fromList.length ? fromList : fromConstraint;
  // AOM2 `constraint` is a single at-/ac-code. ADL 1.4 local value sets
  // arrive as repeated `<code_list>` entries — keep the full list even
  // when that means leaving `constraint` unset.
  if (codes.length === 1) t.constraint = codes[0];
  if (codes.length) t.code_list = codes;

  if (!t.constraint && !t.code_list?.length) {
    const ext = asArray(n.includedExternalTerminologyCodes);
    for (const entry of ext) {
      if (!entry || typeof entry !== "object") continue;
      const code = (entry as Record<string, unknown>).code;
      if (code !== undefined) {
        t.constraint = String(code);
        t.code_list = [t.constraint];
        break;
      }
    }
  }

  const assumed = parseAssumedTerminologyCode(n.assumed_value);
  if (assumed) t.assumed_value = assumed;
  return t;
}

/**
 * ADL 1.4 `C_DV_CODED_TEXT` / `C_CODED_TEXT` often carries a nested
 * `defining_code` (`C_CODE_PHRASE`) rather than a `C_COMPLEX_OBJECT` with a
 * `defining_code` attribute. Lift that into the same AOM shape #69/#70
 * already handle so Web Template `inputs[suffix=code].list` is populated.
 */
function parseCCodedText(n: Record<string, unknown>): openehr_am.C_OBJECT {
  const defining = n.defining_code ?? n.definingCode;
  const nestedAttrs = asArray(n.attributes);
  const ownCodes = asArray(n.code_list);
  if (
    defining || nestedAttrs.length || ownCodes.length ||
    n.terminology_id || n.terminologyId || n.assumed_value
  ) {
    const obj = new openehr_am.C_COMPLEX_OBJECT();
    applyOccurrence(obj, n);
    if (!obj.rm_type_name) obj.rm_type_name = "DV_CODED_TEXT";
    const attributes: openehr_am.C_ATTRIBUTE[] = nestedAttrs.map((attr) =>
      parseAttribute(attr, obj.rm_type_name)
    ).filter(Boolean) as openehr_am.C_ATTRIBUTE[];
    const hasDefining = attributes.some((a) =>
      a.rm_attribute_name === "defining_code"
    );
    if (!hasDefining) {
      const source = (defining && typeof defining === "object")
        ? defining as Record<string, unknown>
        : n;
      const attr = new openehr_am.C_SINGLE_ATTRIBUTE();
      attr.rm_attribute_name = "defining_code";
      (attr as { children?: openehr_am.C_OBJECT[] }).children = [
        parseCTerminologyCode(source),
      ];
      attributes.push(attr);
    }
    obj.attributes = attributes;
    return obj;
  }
  const c = new openehr_am.C_CODED_TEXT();
  applyOccurrence(c, n);
  return c;
}

/** Runtime extras on ADL 1.4 `C_DV_ORDINAL` mapped to AOM2 `C_ORDINAL`. */
type OrdinalRuntime = Omit<openehr_am.C_ORDINAL, "list"> & {
  list?: openehr_am.ORDINAL[];
};

function parseOrdinalSymbol(
  node: unknown,
): openehr_base.CODE_PHRASE | undefined {
  if (!node || typeof node !== "object") return undefined;
  const rec = node as Record<string, unknown>;
  const defining = (rec.defining_code ?? rec.definingCode ?? rec) as Record<
    string,
    unknown
  >;
  const code = textValue(defining.code_string) ??
    (typeof defining.code_string === "string" ||
        typeof defining.code_string === "number"
      ? String(defining.code_string)
      : undefined);
  if (!code) return undefined;
  const tid = terminologyIdValue(
    defining.terminology_id ?? defining.terminologyId ?? rec.terminology_id,
  ) ?? "local";
  return openehr_base.CODE_PHRASE.from(tid, code);
}

function parseCScale(n: Record<string, unknown>): openehr_am.C_SCALE {
  const base = parseCOrdinal(n) as OrdinalRuntime;
  const s = new openehr_am.C_SCALE();
  s.rm_type_name = n.rm_type_name ? String(n.rm_type_name) : "DV_SCALE";
  s.occurrences = base.occurrences;
  if (base.list?.length) (s as OrdinalRuntime).list = base.list;
  if (base.assumed_value) s.assumed_value = base.assumed_value;
  return s;
}

function parseCOrdinal(n: Record<string, unknown>): openehr_am.C_ORDINAL {
  const o = new openehr_am.C_ORDINAL() as OrdinalRuntime;
  applyOccurrence(o, n);
  o.rm_type_name = n.rm_type_name ? String(n.rm_type_name) : "DV_ORDINAL";
  const items: openehr_am.ORDINAL[] = [];
  for (const entry of asArray(n.list)) {
    if (!entry || typeof entry !== "object") continue;
    const rec = entry as Record<string, unknown>;
    const ord = new openehr_am.ORDINAL();
    const value = optionalNumber(rec.value);
    if (value !== undefined) ord.value = value;
    const symbol = parseOrdinalSymbol(rec.symbol ?? rec);
    if (symbol) ord.symbol = symbol;
    items.push(ord);
  }
  if (items.length) o.list = items;
  const assumed = n.assumed_value;
  if (assumed && typeof assumed === "object") {
    const rec = assumed as Record<string, unknown>;
    const av = new openehr_am.ORDINAL();
    const value = optionalNumber(rec.value);
    if (value !== undefined) av.value = value;
    const symbol = parseOrdinalSymbol(rec.symbol ?? rec);
    if (symbol) av.symbol = symbol;
    o.assumed_value = av as unknown as openehr_base.Any;
  }
  return o;
}

/** Assumed DV_QUANTITY fields from ADL 1.4 `<assumed_value>`. */
export interface QuantityAssumedValue {
  magnitude?: number;
  units?: string;
  precision?: number;
}

/** Magnitude / precision interval on an ADL 1.4 `C_QUANTITY_ITEM`. */
export interface QuantityItemInterval {
  lower?: number;
  upper?: number;
  lower_included?: boolean;
  upper_included?: boolean;
  lower_unbounded?: boolean;
  upper_unbounded?: boolean;
}

/** Runtime extras on `C_QUANTITY_ITEM` (BMM types `magnitude` as Interval). */
export type QuantityItemRuntime =
  & Omit<openehr_am.C_QUANTITY_ITEM, "magnitude">
  & {
    magnitude?: QuantityItemInterval;
    precision?: QuantityItemInterval;
  };

function parseNumericInterval(
  node: unknown,
): QuantityItemInterval | undefined {
  if (!node || typeof node !== "object") return undefined;
  const rec = node as Record<string, unknown>;
  const interval: QuantityItemInterval = {};
  const lower = optionalNumber(rec.lower);
  const upper = optionalNumber(rec.upper);
  if (lower !== undefined) interval.lower = lower;
  if (upper !== undefined) interval.upper = upper;
  if (rec.lower_unbounded !== undefined) {
    interval.lower_unbounded = rec.lower_unbounded === true ||
      rec.lower_unbounded === "true";
  }
  if (rec.upper_unbounded !== undefined) {
    interval.upper_unbounded = rec.upper_unbounded === true ||
      rec.upper_unbounded === "true";
  }
  if (rec.lower_included !== undefined) {
    interval.lower_included = rec.lower_included === true ||
      rec.lower_included === "true";
  }
  if (rec.upper_included !== undefined) {
    interval.upper_included = rec.upper_included === true ||
      rec.upper_included === "true";
  }
  if (
    interval.lower === undefined && interval.upper === undefined &&
    interval.lower_unbounded === undefined &&
    interval.upper_unbounded === undefined
  ) {
    return undefined;
  }
  return interval;
}

function optionalNumber(value: unknown): number | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function parseQuantityAssumedValue(
  node: unknown,
): QuantityAssumedValue | undefined {
  if (!node || typeof node !== "object") return undefined;
  const rec = node as Record<string, unknown>;
  const magnitude = optionalNumber(rec.magnitude);
  const precision = optionalNumber(rec.precision);
  const units = textValue(rec.units) ??
    (typeof rec.units === "string" ? rec.units : undefined);
  if (
    magnitude === undefined && precision === undefined && units === undefined
  ) {
    return undefined;
  }
  return { magnitude, units, precision };
}

function parseCQuantity(n: Record<string, unknown>): openehr_am.C_QUANTITY {
  const q = new openehr_am.C_QUANTITY();
  applyOccurrence(q, n);
  q.rm_type_name = "DV_QUANTITY";
  const prop = n.property as Record<string, unknown> | undefined;
  if (prop) {
    q.property = textValue(prop.code_string) ?? textValue(prop);
    const tid = terminologyIdValue(prop.terminology_id ?? prop.terminologyId);
    if (tid) {
      (q as { property_terminology_id?: string }).property_terminology_id = tid;
    }
  }
  const items = asArray(n.list).map((entry) => {
    const item = new openehr_am.C_QUANTITY_ITEM() as QuantityItemRuntime;
    const rec = entry as Record<string, unknown>;
    const units = textValue(rec.units) ??
      (typeof rec.units === "string" ? rec.units : undefined) ??
      (typeof entry === "string" ? entry : undefined);
    if (units !== undefined) item.units = String(units);
    const magnitude = parseNumericInterval(rec.magnitude);
    if (magnitude) item.magnitude = magnitude;
    const precision = parseNumericInterval(rec.precision);
    if (precision) item.precision = precision;
    return item;
  });
  if (items.length) (q as { list?: openehr_am.C_QUANTITY_ITEM[] }).list = items;
  const assumed = parseQuantityAssumedValue(n.assumed_value);
  if (assumed) {
    q.assumed_value = assumed as unknown as openehr_base.Any;
  }
  return q;
}

/** Terms on this XML node only — not descendants (nested C_ARCHETYPE_ROOT). */
export function collectLocalTermDefinitions(
  n: Record<string, unknown>,
): TermBag {
  const bag: TermBag = {};
  for (const td of asArray(n.term_definitions)) {
    const rec = td as Record<string, unknown>;
    const code = String(rec["@_code"] ?? rec.code ?? "");
    if (!code) continue;
    const items = asArray(rec.items);
    const entry: { text?: string; description?: string } = {};
    for (const it of items) {
      const item = it as Record<string, unknown>;
      const id = String(item["@_id"] ?? item.id ?? "");
      const val = textValue(item);
      if (!val) continue;
      if (id === "text") entry.text = val;
      if (id === "description") entry.description = val;
    }
    if (entry.text || entry.description) bag[code] = entry;
  }
  return bag;
}

/**
 * Flatten every `<term_definitions>` in the XML tree into one language bag.
 * Colliding at-codes (the same `at0001` in two archetypes) last-write-wins.
 * Prefer `archetype_term_definitions` + `term_archetype_scope` for lookup.
 */
export function collectTermDefinitions(
  node: unknown,
  bag: Record<string, Record<string, { text?: string; description?: string }>>,
): void {
  if (!node || typeof node !== "object") return;
  const n = node as Record<string, unknown>;
  const local = collectLocalTermDefinitions(n);
  if (Object.keys(local).length) {
    if (!bag.en) bag.en = {};
    Object.assign(bag.en, local);
  }
  for (const child of asArray(n.children)) collectTermDefinitions(child, bag);
  for (const attr of asArray(n.attributes)) {
    for (const c of asArray((attr as Record<string, unknown>).children)) {
      collectTermDefinitions(c, bag);
    }
  }
}
